import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "axios";
import uuid from "react-uuid";
import { useMediaQuery } from "react-responsive";

import { IoInfinite } from "react-icons/io5";
import { RiArrowGoBackFill } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";

import { FormContainer } from "../../../../styled/setting";

import ProductPreview from "./productPreview";
import InputField from "../../../common/form/inputField";
import Switch from "../../../common/form/switch";
import MediaUpload from "../../../common/form/mediaUpload";
import Option from "../../../common/form/option";
import Category from "../../../common/form/category";

import ImageUploadAdapterPlugin from "../../../../../../../../utils/ImageUploadAdapter";
import { getStatusTooltip, status } from "../../../../function/store";

const ProductForm = ({
  product,
  setProduct,
  categories,
  setViewCategory,
  viewCategory,
  categoriesRef,
  categoriesObj,
  setIsDetail,
  options = [],
  setOptions,
  toggleModal,
  changeSlide,
}) => {
  const ckeditorInstanceRef = useRef();

  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  // 첨부 이미지 삭제
  const handleDeleteImage = (item) => {
    setProduct((prevProduct) => ({
      ...prevProduct,
      media: prevProduct.media.filter((img) => img.id !== item.id),
    }));
  };

  // CKEditor 내용 변경
  const handleEditorChange = (_, editor) => {
    const newContent = editor.getData();
    setProduct({ ...product, description: newContent });
  };

  // 이미지 업로드 추가
  const handleAddImage = (e) => {
    const files = Array.from(e.target.files);
    if (product.media.length + files.length > 10) {
      toast.error("이미지는 최대 10장까지 첨부 가능합니다.");
      return;
    }

    if (files.length > 0) {
      const updated = files.map((file) => ({
        file,
        path: URL.createObjectURL(file),
        id: uuid(),
      }));

      setProduct((prev) => ({
        ...prev,
        media: [...prev.media, ...updated],
      }));
    }
  };

  // 입력 처리
  const changeInput = (e) => {
    const { name, value } = e.target;

    if (name === "price") {
      let numericValue = onlyNumber(value);
      numericValue = numericValue === "" ? "" : Math.max(0, parseInt(numericValue, 10));
      setProduct((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setProduct((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 수량 무제한 설정 전환
  const toggleUnlimited = () => {
    setProduct((data) => ({
      ...data,
      quantity: String(data.quantity) === "-1" ? "" : -1,
    }));
  };

  // 태그 입력값 변경
  const changeTag = (e) => {
    const { value } = e.target;
    setProduct((data) => ({ ...data, tag: value }));
  };

  // 태그 입력 후 엔터/쉼표 처리
  const handleKeyDown = (e) => {
    const { tag, tags } = product;
    const trimmedTag = tag && tag.trim();

    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();

      if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 10) {
        setProduct((data) => ({
          ...data,
          tags: [...data.tags, trimmedTag],
          tag: "",
        }));
      }
    }
  };

  // 태그 삭제
  const deleteTag = (index) => {
    setProduct((data) => ({ ...data, tags: [...data.tags].filter((_, idx) => index !== idx) }));
  };

  // 숫자만 입력 허용
  const numericChange = (e) => {
    const { name, value } = e.target;
    let inputValue = toNumber(value);

    setProduct((data) => ({
      ...data,
      [name]: inputValue === "" ? "" : /^\d+$/.test(inputValue) ? inputValue : data[name],
    }));
  };

  // 숫자 포커스 시 쉼표 제거
  const handleFocus = (e) => {
    const { name } = e.target;

    setProduct((data) => ({
      ...data,
      [name]: data[name] ? toNumber(data[name]) : "",
    }));
  };

  //  숫자 포커스 아웃 시 포맷팅
  const handleBlur = (e) => {
    const { name } = e.target;

    setProduct((data) => ({
      ...data,
      [name]: data[name] ? formatNumber(data[name]) : "",
    }));
  };

  const inputFields = [
    {
      label: "상품명",
      name: "name",
      type: "text",
      placeholder: "상품명을 입력해주세요.",
      onChange: changeInput,
      max: 20,
    },
    {
      label: "상품 한줄 설명 (선택)",
      name: "shortDescription",
      type: "text",
      placeholder: "상품 한줄 설명을 입력해주세요. (최대 30자)",
      onChange: changeInput,
      max: 30,
    },
    {
      label: "규격 (선택)",
      name: "specifications",
      type: "text",
      placeholder: "규격을 입력해주세요.",
      onChange: changeInput,
    },
    {
      label: "브랜드명 (선택)",
      name: "brand",
      type: "text",
      placeholder: "브랜드명을 입력해주세요.",
      onChange: changeInput,
      max: 20,
    },
    {
      label: "가격",
      name: "price",
      type: "number",
      max: 6,
      placeholder: "가격을 입력해주세요.",
      onChange: numericChange,
      keyboardEvent: {
        onFocus: handleFocus,
        onBlur: handleBlur,
      },
    },
    {
      label: "수량",
      name: "quantity",
      type: "number",
      placeholder: "상품 수량을 입력해주세요.",
      onChange: numericChange,
      max: 4,
      keyboardEvent: {
        onFocus: handleFocus,
        onBlur: handleBlur,
      },
      children: (
        <button onClick={toggleUnlimited}>
          {String(product.quantity) === "-1" ? <RiArrowGoBackFill /> : <IoInfinite />}
        </button>
      ),
    },
    {
      label: "태그 (선택)",
      name: "tag",
      type: "text",
      placeholder: "태그를 입력한 후 엔터 키를 눌러주세요. (최대 10개)",
      max: 10,
      onChange: changeTag,
      keyboardEvent: {
        onKeyDown: handleKeyDown,
      },
      tooltip: [
        {
          title: "상품 태그",
          description: "상품 태그는 검색 시 노출에 도움이 됩니다.",
          example: "무관한 키워드 입력 시 삭제될 수 있으니, 관련 있는 문구만 입력해 주세요.",
        },
      ],
      children:
        product.tags && product.tags.length > 0 ? (
          <div className="tags">
            {product.tags.map((tag, idx) => (
              <div>
                <span>{tag}</span>
                <button onClick={() => deleteTag(idx)}>
                  <IoMdClose />
                </button>
              </div>
            ))}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="contents">
      <FormContainer>
        {inputFields.map((field) => (
          <InputField key={field.name} label="label" {...field} value={product[field.name]} />
        ))}
        <Category categoriesObj={categoriesObj} product={product} setProduct={setProduct} />
        <Switch
          label="상품 상태"
          keyName="status"
          options={status}
          value={product.status}
          onChange={(value) => setProduct((prev) => ({ ...prev, status: value }))}
          tooltip={getStatusTooltip("메뉴판")}
        />
        <Switch
          label="노출 상태"
          keyName="useDisplay"
          options={{ true: "공개", false: "비공개" }}
          value={!!product.useDisplay}
          onChange={(value) => setProduct((prev) => ({ ...prev, useDisplay: value }))}
        />
        <MediaUpload
          keyName="media"
          label="상품 사진"
          max={10}
          media={product.media}
          setIsDetail={setIsDetail}
          handleAddImage={handleAddImage}
          handleDeleteImage={handleDeleteImage}
          accept="image/*, video/*"
        />

        <li className="description">
          <span className="tit">상품 설명 (선택)</span>
          <div className="cont">
            <CKEditor
              key={`ck-${product?.id}`}
              editor={ClassicEditor}
              data={product.description || ""}
              onChange={handleEditorChange}
              config={{
                extraPlugins: [ImageUploadAdapterPlugin],
                toolbar: ["bold", "italic", "link", "imageUpload"],
              }}
              ref={ckeditorInstanceRef}
              className="custom-ckeditor"
            />
          </div>
        </li>
        <Option
          product={product}
          options={options}
          setOptions={setOptions}
          toggleModal={toggleModal}
          changeSlide={changeSlide}
        />
      </FormContainer>
      {!isMobile && <ProductPreview product={product} options={options} />}
    </div>
  );
};

export default ProductForm;
